import React, { type FC, useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useLocation, Navigate } from 'react-router';
import {
    AppShell,
    Box,
    Button,
    Group,
    NavLink,
    Stack,
    Text,
} from '@mantine/core';
import {
    Package,
    ShoppingCart,
    Truck,
    TrendUpIcon,
    SignOut,
} from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import useApp from '@providers/App/useApp';
import { clearMerchantIdCache } from '@api';
import { clearAllAiThreadCookies } from '../../providers/AiAnalyst/useAiChatThreadPersistence';
import { clearAllAgentSessionStorage } from '@hooks/useAgentChat';
import { clearQuickHelpSessionStorage } from '@providers/QuickHelp/QuickHelpProvider';
import { ConsultantCapability } from '@constants/consultant-capability';

/**
 * Navigation items with their required capabilities.
 * A consultant sees a nav item if they have ANY of the listed capabilities.
 */
const CAPABILITY_NAV: Array<{
    label: string;
    path: string;
    icon: React.ElementType;
    capabilities: string[];
}> = [
    {
        label: 'Suppliers',
        path: '/suppliers',
        icon: Truck,
        capabilities: [ConsultantCapability.TABLE_SUPPLIERS_VENDOR_LIST],
    },
    {
        label: 'Purchase Orders',
        path: '/purchase-orders',
        icon: ShoppingCart,
        capabilities: [
            ConsultantCapability.TABLE_PO_DEMAND,
            ConsultantCapability.TABLE_PO_SUPPLY,
        ],
    },
    {
        label: 'Inventory',
        path: '/inventory',
        icon: Package,
        capabilities: [
            ConsultantCapability.TABLE_INVENTORY_SNAPSHOT,
            ConsultantCapability.TABLE_INVENTORY_PLANNING,
            ConsultantCapability.TABLE_INVENTORY_PIPELINE,
        ],
    },
    {
        label: 'Demand',
        path: '/forecasts',
        icon: TrendUpIcon,
        capabilities: [
            ConsultantCapability.TABLE_DEMAND_FORECAST,
            ConsultantCapability.TABLE_DEMAND_CHART,
        ],
    },
];

/**
 * Get all routes the consultant is allowed to access based on their capabilities.
 */
function getAllowedRoutes(consultantAccess: string[]): string[] {
    return CAPABILITY_NAV.filter((item) =>
        item.capabilities.some((cap) => consultantAccess.includes(cap)),
    ).map((item) => item.path);
}

/**
 * Check if a given path is allowed for the consultant.
 */
function isPathAllowed(pathname: string, allowedRoutes: string[]): boolean {
    // Exact match or starts with (for nested routes)
    return allowedRoutes.some(
        (route) => pathname === route || pathname.startsWith(`${route}/`),
    );
}

const ConsultantLayout: FC = () => {
    const { currentUser, consultantAccess } = useApp();
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();

    // Calculate allowed routes based on consultant's access
    const allowedRoutes = useMemo(
        () => getAllowedRoutes(consultantAccess),
        [consultantAccess],
    );

    // Get the first allowed route (for redirects)
    const firstAllowedRoute = allowedRoutes[0] || '/login';

    // Check if current path is allowed
    const currentPathAllowed = isPathAllowed(location.pathname, allowedRoutes);

    // Visible nav items for sidebar
    const visibleNav = CAPABILITY_NAV.filter((item) =>
        item.capabilities.some((cap) => consultantAccess.includes(cap)),
    );

    // Redirect if current path is not allowed
    if (!currentPathAllowed && allowedRoutes.length > 0) {
        return <Navigate to={firstAllowedRoute} replace />;
    }

    // If no allowed routes at all, redirect to login
    if (allowedRoutes.length === 0) {
        return <Navigate to="/login" replace />;
    }

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        clearMerchantIdCache();
        // Clear AI chat thread cookies and agent session data to prevent cross-merchant persistence
        clearAllAiThreadCookies();
        clearAllAgentSessionStorage();
        clearQuickHelpSessionStorage();
        // Clear React Query cache to prevent stale user data
        queryClient.clear();
        navigate('/login');
    };

    const fullName =
        [currentUser?.firstName, currentUser?.lastName]
            .filter(Boolean)
            .join(' ') ||
        currentUser?.email ||
        'Consultant';

    return (
        <AppShell
            navbar={{ width: 200, breakpoint: 'sm' }}
            header={{ height: 52 }}
        >
            <AppShell.Header>
                <Group h="100%" px="md" justify="space-between">
                    <Text fw={700} size="lg" c="blue">
                        Reparo
                    </Text>
                    <Group gap="xs">
                        <Text size="sm" c="dimmed">
                            {fullName}
                        </Text>
                        <Button
                            variant="subtle"
                            size="xs"
                            leftSection={<SignOut size={14} />}
                            onClick={handleLogout}
                        >
                            Logout
                        </Button>
                    </Group>
                </Group>
            </AppShell.Header>

            <AppShell.Navbar p="xs">
                <Stack gap={4}>
                    {visibleNav.map((item) => (
                        <NavLink
                            key={item.path}
                            label={item.label}
                            leftSection={<item.icon size={16} />}
                            active={location.pathname === item.path}
                            onClick={() => navigate(item.path)}
                        />
                    ))}
                </Stack>
            </AppShell.Navbar>

            <AppShell.Main>
                <Box h="calc(100vh - 52px)" style={{ overflow: 'auto' }}>
                    <Outlet />
                </Box>
            </AppShell.Main>
        </AppShell>
    );
};

export default ConsultantLayout;
